import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { faChevronLeft, faBookOpen, faLightbulb } from '@fortawesome/free-solid-svg-icons';

export interface ReadinessHotspot {
  id: string;
  label: string;
  title: string;
  short: string;
  detailImage?: string;
  steps: string[];
  tip?: string;
  tags: string[];
  top: number;
  left: number;
  section?: string | null;
  icon?: string;
  linkedSection?: string;
}

export interface Section {
  id: string;
  title: string;
  image: string;
  top: number;
  left: number;
  description: string;
}

@Component({
  standalone: false,
  selector: 'app-readiness-guide',
  templateUrl: './readiness-guide.component.html',
  styleUrls: ['./readiness-guide.component.scss'],
  animations: [
    trigger('slideIn', [
      transition('* => *', [
        style({ opacity: 0, transform: 'translateX(20px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateX(0)' }))
      ])
    ]),
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('400ms ease-out', style({ opacity: 1 }))
      ])
    ])
  ]
})
export class ReadinessGuideComponent implements OnInit {

  currentSection: string | null = null;
  searchTerm = '';
  hoveredHotspot: string | null = null;
  activeCategory: string = 'all';
  faChevronLeft = faChevronLeft;
  faBookOpen = faBookOpen;
  faLightbulb = faLightbulb;

  // Categories for filtering
  categories = [
    { id: 'all', label: 'All Features', icon: 'th-large' },
    { id: 'navigation', label: 'Navigation', icon: 'compass' },
    { id: 'editing', label: 'Editing', icon: 'edit' },
    { id: 'data', label: 'Data Management', icon: 'database' },
    { id: 'collaboration', label: 'Collaboration', icon: 'users' }
  ];

  // Section definitions - aligned based on screenshot
  // In overview image, sections appear as column headers at row 36% from top
  sections: Section[] = [
    {
      id: 'tar-section',
      title: 'Approved for TAR',
      image: 'app/assets/readiness-guide/section-tar.png',
      top: 20,
      left: 26.5,
      description: 'Technical Authority Release approval tracking'
    },
    {
      id: 'tr-section',
      title: 'TR Status',
      image: 'app/assets/readiness-guide/section-tr.png',
      top: 20,
      left: 30,
      description: 'Technical Review status indicators'
    },
    {
      id: 'wp-section',
      title: 'WP Status',
      image: 'app/assets/readiness-guide/section-wp.png',
      top: 20,
      left: 34,
      description: 'Work Package status tracking'
    },
    {
      id: 'materials-section',
      title: 'Materials',
      image: 'app/assets/readiness-guide/section-materials.png',
      top: 20,
      left: 38.5,
      description: 'Materials and procurement tracking'
    },
    {
      id: 'moc-section',
      title: 'MOC',
      image: 'app/assets/readiness-guide/section-moc.png',
      top: 20,
      left: 42,
      description: 'Management of Change approvals'
    },
    {
      id: 'task-checked',
      title: 'Selected Row Actions',
      image: 'app/assets/readiness-guide/task-checked.png',
      top: 40,
      left: 3,
      description: 'Actions available when a task row is selected'
    },
    {
      id: 'attachments-grid-section',
      title: 'Open Task Attachments',
      image: 'app/assets/readiness-guide/attachment-grid-view.png',
      top: 40,
      left: 3,
      description: 'How to open per-task attachments and image dialog from the grid'
    },
    {
      id: 'task-image-modal-section',
      title: 'Manage Task Image',
      image: 'app/assets/readiness-guide/attachment-modal-existing-image.png',
      top: 40,
      left: 3,
      description: 'Preview, replace, remove the existing task image and manage attachments'
    },
    {
      id: 'task-image-upload-modal-section',
      title: 'Upload Task Image',
      image: 'app/assets/readiness-guide/attachment-modal-upload-state.png',
      top: 40,
      left: 3,
      description: 'Drag/drop, upload from PC, or fetch image from ProPak'
    }
  ];

  // Complete list of all features from the readiness form
  hotspots: ReadinessHotspot[] = [
    // ========================================
    // OVERVIEW-LEVEL FEATURES (section: null)
    // These appear on the main overview image
    // ========================================
    {
      id: 'task-toggle',
      label: '1',
      title: 'Task View Toggle',
      short: 'Switch between current, archived, or both task views.',
      steps: [
        'Click "CURRENT TASKS" button to view only active tasks.',
        'Click "ARCHIVED TASKS" to see completed/archived tasks.',
        'Click "BOTH" to display all tasks together.',
        'The archived count shows in parentheses.',
        'View updates instantly when toggled.'
      ],
      tip: 'Use archived view to reference completed work without cluttering your current task list.',
      tags: ['toggle', 'current', 'archived', 'view', 'tasks', 'filter', 'navigation'],
      top: 20,
      left: 8,
      section: null,
      icon: 'toggle-on'
    },
    {
      id: 'add-task',
      label: '2',
      title: 'Add New Task',
      short: 'Create a new task row in the readiness form.',
      steps: [
        'Click the "+" button in the toolbar.',
        'A new row appears at the bottom of the grid.',
        'Task number auto-increments from the highest existing number.',
        'Fill in required fields (marked with red borders).',
        'Click Save to persist the new task.'
      ],
      tip: 'You can also add tasks inline by hovering over any row and clicking the "+" icon that appears.',
      tags: ['add', 'new', 'task', 'create', 'plus', 'insert', 'editing'],
      top: 13.5,
      left: 2.5,
      section: null,
      icon: 'plus-circle'
    },
    {
      id: 'open-propak',
      label: '3',
      title: 'Open in ProPak',
      short: 'Open selected task in ProPak WorkPack application.',
      steps: [
        'Select a single task row using checkbox.',
        'Click the wrench icon button.',
        'If the work package exists in ProPak, it opens to the selected work package.',
        'If the work package is missing in ProPak but exists in ClientDash, it will be automatically added to ProPak and then opened.',
        'View detailed materials and scope.',
        'Changes in ProPak sync back to readiness form.'
      ],
      tip: 'Use this for detailed work package editing that requires ProPak features. Work packages are automatically created in ProPak if needed.',
      tags: ['propak', 'workpack', 'open', 'integration', 'data'],
      top: 13.5,
      left: 5,
      section: null,
      icon: 'wrench'
    },
    {
      id: 'undo-redo',
      label: '4',
      title: 'Undo & Redo',
      short: 'Revert or restore changes with undo/redo buttons.',
      steps: [
        'Click Undo button (↶) to revert the last change.',
        'Click Redo button (↷) to restore an undone change.',
        'Multiple levels of undo/redo are supported.',
        'Changes are tracked per editing session.',
        'Undo history clears when you save or refresh.'
      ],
      tip: 'Use Ctrl+Z for undo and Ctrl+Y for redo keyboard shortcuts!',
      tags: ['undo', 'redo', 'revert', 'restore', 'history', 'editing'],
      top: 13.5,
      left: 8.5,
      section: null,
      icon: 'undo'
    },
    {
      id: 'active-filters',
      label: '5',
      title: 'Active Filters Display',
      short: 'Shows currently applied column filters.',
      steps: [
        'Active filters appear as "Active Filters:" label.',
        'Each filtered column name is listed.',
        'Click "Reset Filters" to clear all filters at once.',
        'Individual filters can be cleared from column headers.',
        'Filters persist until manually cleared.'
      ],
      tip: 'Clear filters before exporting to ensure you get all data in your report.',
      tags: ['filter', 'active', 'display', 'reset', 'clear', 'data'],
      top: 13.5,
      left: 14,
      section: null,
      icon: 'filter'
    },
    {
      id: 'propak-toggle',
      label: '6',
      title: 'ProPak Integration Toggle',
      short: 'Toggle ProPak work packages display and integration.',
      steps: [
        'Toggle "Show ProPak Work Packs" switch to enable.',
        'ProPak work packages become available for import.',
        'Select a task row and click wrench icon to open in ProPak.',
        'Materials data syncs from ProPak automatically.',
        'Toggle off to hide ProPak integration features.'
      ],
      tip: 'Keep ProPak integration enabled during materials tracking to see real-time status updates.',
      tags: ['propak', 'integration', 'toggle', 'workpack', 'materials', 'sync', 'data'],
      top: 13.5,
      left: 18,
      section: null,
      icon: 'sync'
    },
    {
      id: 'collaborators',
      label: '7',
      title: 'Active Collaborators',
      short: 'See who else is currently editing the form.',
      steps: [
        '"Also editing:" shows other users in the form.',
        'User names appear when they make changes.',
        'Real-time updates via SignalR connection.',
        'Helps avoid conflicting edits.',
        'Users disappear when they leave the form.'
      ],
      tip: 'Coordinate with collaborators before making major changes to avoid conflicts.',
      tags: ['collaboration', 'users', 'editing', 'realtime', 'signalr', 'team'],
      top: 13.5,
      left: 38,
      section: null,
      icon: 'users'
    },
    {
      id: 'reset-filters',
      label: '8',
      title: 'Reset Filters',
      short: 'Clear all column filters at once.',
      steps: [
        'Click "Reset Filters" button.',
        'All column filters are cleared instantly.',
        'All rows become visible again.',
        'Button only appears when filters are active.',
        'Useful for starting a fresh data view.'
      ],
      tip: 'Reset filters before saving to ensure all your changes are captured.',
      tags: ['reset', 'filter', 'clear', 'all', 'data'],
      top: 13.5,
      left: 60,
      section: null,
      icon: 'times-circle'
    },
    {
      id: 'delete-task',
      label: '9',
      title: 'Delete Tasks',
      short: 'Remove selected tasks from the form.',
      steps: [
        'Select one or more tasks using checkboxes.',
        'Click the red trash icon button.',
        'Confirm deletion when prompted.',
        'Deleted tasks cannot be recovered.',
        'Save to persist the deletion.'
      ],
      tip: 'Consider archiving tasks instead of deleting to preserve historical data.',
      tags: ['delete', 'remove', 'trash', 'task', 'editing'],
      top: 13.5,
      left: 84,
      section: null,
      icon: 'trash'
    },
    {
      id: 'save-button',
      label: '10',
      title: 'Save Changes',
      short: 'Persist all modifications to the database.',
      steps: [
        'Click "Save" button to commit all changes.',
        'Validation runs before saving.',
        'Duplicate task numbers prevent save.',
        'Missing required fields show errors.',
        'Success notification confirms save.'
      ],
      tip: 'Save frequently when multiple users are working to avoid conflicts!',
      tags: ['save', 'persist', 'commit', 'database', 'editing'],
      top: 13.5,
      left: 87.5,
      section: null,
      icon: 'save'
    },
    {
      id: 'export-excel',
      label: '11',
      title: 'Export to Excel',
      short: 'Download the readiness form as an Excel file.',
      steps: [
        'Click "Export" button with the file icon.',
        'Current filters and grouping are applied.',
        'Excel file downloads automatically.',
        'All visible columns are included.',
        'Great for sharing with stakeholders.'
      ],
      tip: 'Apply filters before exporting to create focused reports for specific reviews.',
      tags: ['export', 'excel', 'download', 'report', 'data'],
      top: 13.5,
      left: 91,
      section: null,
      icon: 'file-excel'
    },
    {
      id: 'archive-task',
      label: '12',
      title: 'Archive/Unarchive Tasks',
      short: 'Move completed tasks to archive or restore them.',
      steps: [
        'Select completed tasks using checkboxes.',
        'Click "Archive Task" button to archive.',
        'Archived tasks move to archived view.',
        'Switch to archived view to see them.',
        'Select archived tasks and click "Unarchive" to restore.'
      ],
      tip: 'Archive completed tasks to keep your current view clean and focused.',
      tags: ['archive', 'unarchive', 'complete', 'task', 'data'],
      top: 13.5,
      left: 95,
      section: null,
      icon: 'archive'
    },
    {
      id: 'remove-grouping',
      label: '13',
      title: 'Remove Grouping',
      short: 'Clear row grouping and return to flat view.',
      steps: [
        'Button appears when grouping is active.',
        'Click "Remove Grouping" to clear.',
        'Rows return to normal flat display.',
        'Sorting is preserved after ungrouping.',
        'Group again by clicking column group icons.'
      ],
      tip: 'Remove grouping before exporting for a cleaner spreadsheet layout.',
      tags: ['grouping', 'remove', 'clear', 'view', 'data'],
      top: 13.5,
      left: 80,
      section: null,
      icon: 'layer-group'
    },
    {
      id: 'settings',
      label: '14',
      title: 'Readiness Form Settings',
      short: 'Configure form columns, sections, and traffic lights.',
      steps: [
        'Click the gear icon (admin only).',
        'Configure column visibility and order.',
        'Set up traffic light rules.',
        'Manage section configurations.',
        'Changes apply to all users.'
      ],
      tip: 'Only administrators can access form settings. Contact your admin for changes.',
      tags: ['settings', 'admin', 'configure', 'columns', 'data'],
      top: 13.5,
      left: 98.5,
      section: null,
      icon: 'cog'
    },
    {
      id: 'row-selection',
      label: '6',
      title: 'Row Selection Checkbox',
      short: 'Select rows to enable actions like delete, archive, or open in ProPak.',
      steps: [
        'Click checkbox at row start to select that row.',
        'Use header checkbox to select all visible rows.',
        'Selected rows enable toolbar actions: Delete, Archive, Open in ProPak.',
        'Click wrench icon to open selected task in ProPak.',
        'Deselect by clicking checkbox again.'
      ],
      tip: 'Select a task and click the wrench icon to jump directly to that work package in ProPak!',
      tags: ['select', 'checkbox', 'bulk', 'delete', 'archive', 'propak', 'editing'],
      top: 38,
      left: 3,
      section: null,
      icon: 'check-square',
      linkedSection: 'task-checked'
    },
    {
      id: 'row-actions',
      label: '7',
      title: 'Row Hover Actions',
      short: 'Drag handle and inline add appear on row hover.',
      steps: [
        'Hover over any row to reveal action icons.',
        'Drag handle (⋮⋮) appears for reordering rows.',
        'Plus icon (+) appears to add a new row below.',
        'Click and drag the handle to move the row.',
        'Click + to insert a new task at that position.',
        'For multi-row drag, select multiple rows first and then drag selected rows.'
      ],
      tip: 'Use drag and drop to quickly prioritize tasks by moving them up or down!',
      tags: ['drag', 'drop', 'reorder', 'add', 'insert', 'hover', 'editing'],
      top: 49.5,
      left: 3,
      section: null,
      icon: 'grip-vertical'
    },
    {
      id: 'pin-columns',
      label: '17',
      title: 'Pin Left Columns',
      short: 'Fix left columns while scrolling horizontally.',
      steps: [
        'Click the pin/thumbtack icon.',
        'Left columns become fixed/sticky.',
        'Scroll right to see other columns.',
        'Fixed columns stay visible.',
        'Click again to unpin.'
      ],
      tip: 'Pin columns to keep task identifiers visible while editing details.',
      tags: ['pin', 'sticky', 'fixed', 'scroll', 'navigation'],
      top: 20.5,
      left: 22,
      section: null,
      icon: 'thumbtack'
    },
    {
      id: 'progress-status',
      label: '18',
      title: 'Progress Status Bar',
      short: 'Visual progress indicator showing completion percentage.',
      steps: [
        'Each row has a status progress bar.',
        'Percentage based on filled required fields.',
        'Color changes: red → yellow → green.',
        'Updates automatically as you fill fields.',
        '100% means all required fields complete.'
      ],
      tip: 'Sort by Status column to focus on tasks needing the most attention.',
      tags: ['progress', 'status', 'percentage', 'completion', 'data'],
      top: 47,
      left: 64,
      section: null,
      icon: 'tasks'
    },

    // ========================================
    // TAR SECTION FEATURES
    // ========================================
    {
      id: 'tar-toggle-workpacks',
      label: '1',
      title: 'Toggle WorkPacks',
      short: 'Sync work packages from ProPak when no tasks exist.',
      steps: [
        'This button appears when readiness form has no tasks.',
        'Click "Toggle WorkPacks" to fetch from ProPak.',
        'Work packages from the same project are imported.',
        'Each work package becomes a task row.',
        'After sync, you can edit tasks normally.'
      ],
      tip: 'Use this to quickly populate a new readiness form with existing ProPak work packages.',
      tags: ['toggle', 'workpacks', 'propak', 'sync', 'import', 'data'],
      top: 35,
      left: 25,
      section: 'tar-section',
      icon: 'sync'
    },
    {
      id: 'tar-inline-editing',
      label: '2',
      title: 'Inline Editing',
      short: 'Edit TAR approval directly in the cell.',
      steps: [
        'Click any editable cell in the TAR section.',
        'For checkboxes, click to toggle approval status.',
        'Changed cells highlight in yellow until saved.',
        'Press Tab to move to the next editable cell.',
        'All changes are tracked for undo/redo.'
      ],
      tip: 'Yellow highlighting helps you track all unsaved changes before saving.',
      tags: ['edit', 'inline', 'cell', 'tar', 'editing'],
      top: 50,
      left: 30,
      section: 'tar-section',
      icon: 'edit'
    },
    {
      id: 'tar-traffic-light',
      label: '3',
      title: 'Traffic Light Status',
      short: 'Visual indicator showing TAR section completion.',
      steps: [
        'Green circle = All TAR requirements complete.',
        'Yellow/Amber = Partial completion.',
        'Red = Missing required TAR approvals.',
        'Status updates automatically as you edit.',
        'Rules configured by admin in settings.'
      ],
      tip: 'Scan traffic lights to quickly identify tasks needing TAR approval.',
      tags: ['traffic', 'light', 'status', 'indicator', 'data'],
      top: 65,
      left: 80.5,
      section: 'tar-section',
      icon: 'circle'
    },

    // ========================================
    // TR STATUS SECTION FEATURES
    // ========================================
    {
      id: 'column-filter',
      label: '1',
      title: 'Column Filtering',
      short: 'Filter rows to show only specific values.',
      steps: [
        'Click the filter icon (funnel) in any column header.',
        'Check the values you want to display.',
        'Click "Apply" to activate the filter.',
        'Filtered columns show blue filter icon.',
        'Use "Reset Filters" button to clear all filters.'
      ],
      tip: 'Combine multiple column filters for precise data views during reviews.',
      tags: ['filter', 'column', 'search', 'data'],
      top: 28.5,
      left: 30.5,
      section: 'tr-section',
      icon: 'filter'
    },
    {
      id: 'column-sort',
      label: '2',
      title: 'Column Sorting',
      short: 'Sort rows ascending or descending by any column.',
      steps: [
        'Click any column header text to sort.',
        'First click = ascending order (↑ arrow appears).',
        'Second click = descending order (↓ arrow appears).',
        'Third click = removes sorting.',
        'Only one column can be sorted at a time.'
      ],
      tip: 'Sort by Status or Priority to focus on critical tasks first.',
      tags: ['sort', 'column', 'ascending', 'descending', 'data'],
      top: 29,
      left: 44,
      section: 'tr-section',
      icon: 'sort'
    },
    {
      id: 'tr-traffic-light',
      label: '3',
      title: 'Traffic Light Status',
      short: 'Visual indicator showing TR section completion.',
      steps: [
        'Green = Technical Review complete.',
        'Yellow = Review in progress.',
        'Red = Review not started or issues.',
        'Based on configured traffic light rules.',
        'Updates automatically when fields change.'
      ],
      tip: 'Traffic lights provide instant visual status across the entire form.',
      tags: ['traffic', 'light', 'status', 'data'],
      top: 63,
      left: 58,
      section: 'tr-section',
      icon: 'circle'
    },

    // ========================================
    // WP STATUS SECTION FEATURES
    // ========================================
    {
      id: 'column-grouping',
      label: '1',
      title: 'Row Grouping',
      short: 'Group rows by column values for organized views.',
      steps: [
        'Click the layer/stack icon in any column header.',
        'Rows organize into collapsible groups.',
        'Group header shows the value and row count.',
        'Click group header to expand/collapse.',
        'Click "Remove Grouping" button to return to flat view.'
      ],
      tip: 'Group by WP Status during reviews to discuss tasks by workflow stage.',
      tags: ['group', 'column', 'organize', 'category', 'data'],
      top: 28,
      left: 41,
      section: 'wp-section',
      icon: 'layer-group'
    },
    {
      id: 'column-resize',
      label: '2',
      title: 'Column Resizing',
      short: 'Drag column borders to adjust width.',
      steps: [
        'Hover over the border between two column headers.',
        'Cursor changes to a resize indicator.',
        'Click and drag left or right.',
        'Release to set the new width.',
        'Widths persist during your session.'
      ],
      tip: 'Resize columns to see more content in cells with long text.',
      tags: ['resize', 'column', 'width', 'navigation'],
      top: 50,
      left: 42.7,
      section: 'wp-section',
      icon: 'arrows-alt-h'
    },
    {
      id: 'wp-traffic-light',
      label: '3',
      title: 'Traffic Light Status',
      short: 'Visual indicator showing WP section completion.',
      steps: [
        'Green = Work Package complete/approved.',
        'Yellow = In progress.',
        'Red = Not started or has issues.',
        'Based on traffic light rules in admin settings.',
        'Provides quick visual scan of readiness.'
      ],
      tip: 'Use traffic lights for quick executive-level status reporting.',
      tags: ['traffic', 'light', 'status', 'data'],
      top: 68,
      left: 44.1,
      section: 'wp-section',
      icon: 'circle'
    },

    // ========================================
    // MATERIALS SECTION FEATURES
    // ========================================
    {
      id: 'inline-editing',
      label: '1',
      title: 'Inline Cell Editing',
      short: 'Edit any cell directly without opening a form.',
      steps: [
        'Click any editable cell to activate edit mode.',
        'Type new values for text fields.',
        'Select from dropdown for choice fields.',
        'Changed cells highlight in yellow.',
        'Press Tab to navigate between cells.'
      ],
      tip: 'Yellow highlighting helps track all unsaved changes across the form.',
      tags: ['edit', 'inline', 'cell', 'input', 'editing'],
      top: 40,
      left: 55,
      section: 'materials-section',
      icon: 'edit'
    },
    {
      id: 'materials-traffic-light',
      label: '2',
      title: 'Traffic Light Status',
      short: 'Visual indicator showing materials readiness.',
      steps: [
        'Green = All materials ready/available.',
        'Yellow = Partially available.',
        'Red = Materials not ready - blocks execution.',
        'Status based on ProPak integration data.',
        'Critical for execution go/no-go decisions.'
      ],
      tip: 'Red materials traffic lights may block execution - address early!',
      tags: ['traffic', 'light', 'status', 'materials', 'data'],
      top: 56,
      left: 63.5,
      section: 'materials-section',
      icon: 'circle'
    },

    // ========================================
    // MOC SECTION FEATURES
    // ========================================
    {
      id: 'section-collapse',
      label: '1',
      title: 'Section Collapse/Expand',
      short: 'Click section headers to collapse or expand columns.',
      steps: [
        'Click on any colored section header.',
        'Section columns collapse to save horizontal space.',
        'Traffic light remains visible when collapsed.',
        'Click header again to expand.',
        'Helps focus on sections you are working on.'
      ],
      tip: 'Collapse completed sections to focus on areas needing attention.',
      tags: ['collapse', 'expand', 'section', 'view', 'navigation'],
      top: 18,
      left: 58,
      section: 'moc-section',
      icon: 'compress-arrows-alt'
    },
    {
      id: 'change-tracking',
      label: '2',
      title: 'Change Tracking',
      short: 'Visual indicators show modified cells.',
      steps: [
        'Edited cells highlight in yellow.',
        'Yellow remains until changes are saved.',
        'Helps identify what needs to be saved.',
        'Undo/Redo work on tracked changes.',
        'Saving clears all yellow highlights.'
      ],
      tip: 'Always review yellow-highlighted cells before saving to verify changes.',
      tags: ['change', 'tracking', 'highlight', 'editing', 'data'],
      top: 50,
      left: 70,
      section: 'moc-section',
      icon: 'highlighter'
    },
    {
      id: 'moc-traffic-light',
      label: '3',
      title: 'Traffic Light Status',
      short: 'Visual indicator showing MOC section completion.',
      steps: [
        'Green = MOC approved and complete.',
        'Yellow = Pending approval.',
        'Red = MOC required but not approved.',
        'Updates automatically when checkbox changes.',
        'Critical gate for execution phase.'
      ],
      tip: 'MOC traffic light must be green before task can proceed to execution.',
      tags: ['traffic', 'light', 'status', 'moc', 'data'],
      top: 62,
      left: 58.2,
      section: 'moc-section',
      icon: 'circle'
    },

    // ========================================
    // ATTACHMENTS & TASK IMAGE (overview-level entry pings)
    // Each click swaps the main central image to the linked section
    // and shows sub-hotspots highlighting interactive features.
    // ========================================
    {
      id: 'highlight-changed-cell',
      label: 'H',
      title: 'Highlighted Edited Cell',
      short: 'Orange border around a cell shows it has unsaved edits pending Save.',
      steps: [
        'Edit any cell in the grid (e.g., Description in row 1).',
        'Cell border turns orange to mark it as a changed/unsaved cell.',
        'All edited cells stay highlighted until you click Save.',
        'Use highlights to scan exactly what will be persisted.',
        'Click Save (toolbar) to commit; highlights then clear.'
      ],
      tip: 'Always review all orange-bordered cells before saving to confirm intentional edits.',
      tags: ['highlight', 'change', 'edit', 'unsaved', 'cell', 'editing', 'data'],
      top: 38,
      left: 20,
      section: null,
      icon: 'highlighter'
    },
    {
      id: 'attachments-folder',
      label: 'A1',
      title: 'Open Task Attachments',
      short: 'See where to open per-task attachments and image dialog from the grid.',
      steps: [
        'Click this ping to open the attachments grid view.',
        'Sub-pings will highlight folder icon, file count, and image thumbnail.',
        'Use those entry points in the form to open the attachment modal.',
        'Manage task image and attached files for the selected row.',
        'Click modal Save, then form Save, to persist changes.'
      ],
      tip: 'Always confirm the task number in the modal title before uploading files.',
      tags: ['attachment', 'file', 'folder', 'open', 'editing', 'data'],
      top: 83,
      left: 7,
      section: null,
      icon: 'folder-open',
      linkedSection: 'attachments-grid-section'
    },
    {
      id: 'task-image-thumbnail',
      label: 'A2',
      title: 'Manage Existing Task Image',
      short: 'Open the attachment modal to preview, replace, or remove the existing task image.',
      steps: [
        'Click this ping to open the modal showing an existing task image.',
        'Sub-pings will highlight image preview, source badges, remove control, and modal save flow.',
        'Replace image if it does not match the current task context.',
        'Use modal Save and then form Save to persist updates.',
        'Source badges help track ProPak vs ClientDash origin.'
      ],
      tip: 'Use this view to learn how to manage an already-set task image.',
      tags: ['image', 'manage', 'preview', 'task', 'editing', 'data'],
      top: 54,
      left: 9,
      section: null,
      icon: 'image',
      linkedSection: 'task-image-modal-section'
    },
    {
      id: 'task-image-upload',
      label: 'A3',
      title: 'Upload Task Image',
      short: 'See upload state of attachment modal: drag/drop, ProPak fetch, and supported formats.',
      steps: [
        'Click this ping to switch to attachment modal upload state.',
        'Sub-pings highlight ProPak toggle, drag/drop image area, and upload button.',
        'Allowed formats: PNG, JPG, JPEG, GIF, WEBP.',
        'Or toggle "Get image from ProPak" to fetch reference image.',
        'Save the modal, then save the readiness form.'
      ],
      tip: 'Use this view to discover all task image entry options in upload state.',
      tags: ['image', 'upload', 'propak', 'drag', 'drop', 'editing', 'data'],
      top: 66,
      left: 5,
      section: null,
      icon: 'upload',
      linkedSection: 'task-image-upload-modal-section'
    },
    // {
    //   id: 'attachment-modal-workflow',
    //   label: 'A4',
    //   title: 'Attachment Modal Save Flow',
    //   short: 'See how to add files, remove items, and save changes correctly in the attachment modal.',
    //   steps: [
    //     'Click this ping to load the attachment modal save flow.',
    //     'Sub-pings highlight Add files, drop zone, Open/Remove, and modal Save/Cancel.',
    //     'Always click modal Save first to apply changes to the row.',
    //     'Then save the readiness form to persist updates to backend.',
    //     'Skipping either Save loses changes.'
    //   ],
    //   tip: 'Use this view to understand the correct order: modal Save → form Save.',
    //   tags: ['attachment', 'modal', 'add', 'remove', 'save', 'cancel', 'workflow', 'data'],
    //   top: 70,
    //   left: 5,
    //   section: null,
    //   icon: 'save',
    //   linkedSection: 'task-image-modal-section'
    // },

    // ========================================
    // TASK CHECKED (SELECTED ROW) FEATURES
    // ========================================
    {
      id: 'checked-delete',
      label: '1',
      title: 'Delete Selected Tasks',
      short: 'Remove selected tasks from the readiness form.',
      steps: [
        'Select one or more tasks using checkboxes.',
        'The delete button (trash icon) becomes active.',
        'Click the trash icon button.',
        'Confirm deletion when prompted.',
        'Save to persist the deletion.'
      ],
      tip: 'Consider archiving tasks instead of deleting to preserve historical data.',
      tags: ['delete', 'remove', 'trash', 'task', 'selected', 'editing'],
      top: 11,
      left: 86.5,
      section: 'task-checked',
      icon: 'trash'
    },
    {
      id: 'checked-archive',
      label: '2',
      title: 'Archive Selected Tasks',
      short: 'Move selected tasks to the archive.',
      steps: [
        'Select completed tasks using checkboxes.',
        'The archive button becomes active.',
        'Click "Archive Task" button.',
        'Tasks move to archived view.',
        'Switch to "ARCHIVED TASKS" to see them.'
      ],
      tip: 'Archive completed tasks to keep your current view clean and focused.',
      tags: ['archive', 'complete', 'task', 'selected', 'data'],
      top: 11,
      left: 95,
      section: 'task-checked',
      icon: 'archive'
    },
    {
      id: 'checked-propak',
      label: '3',
      title: 'Open in ProPak',
      short: 'Open the selected task in ProPak application.',
      steps: [
        'Select a single task row using checkbox.',
        'The wrench icon button becomes active.',
        'Click the wrench icon.',
        'ProPak opens to the selected work package.',
        'View and edit detailed materials and scope.'
      ],
      tip: 'ProPak opens in a new tab - changes sync back automatically.',
      tags: ['propak', 'workpack', 'open', 'selected', 'integration', 'data'],
      top: 11,
      left: 4,
      section: 'task-checked',
      icon: 'wrench'
    },
    {
      id: 'checked-row-highlight',
      label: '4',
      title: 'Selected Row Highlight',
      short: 'Visual indicator showing which rows are selected.',
      steps: [
        'Selected rows have a highlighted background.',
        'Checkbox shows as checked.',
        'Multiple rows can be selected at once.',
        'Click checkbox again to deselect.',
        'Use header checkbox to select/deselect all.'
      ],
      tip: 'The selection count appears in the toolbar when rows are selected.',
      tags: ['select', 'highlight', 'row', 'checkbox', 'editing'],
      top: 32,
      left: 2.5,
      section: 'task-checked',
      icon: 'check-square'
    },

    // ========================================
    // ATTACHMENTS GRID SECTION FEATURES
    // (Sub-hotspots shown after clicking A1)
    // ========================================
    {
      id: 'grid-folder-icon',
      label: '1',
      title: 'Folder Icon (Open Attachments)',
      short: 'Folder icon under each task number opens the attachment modal for that row.',
      steps: [
        'Find the blue folder icon under any task number in Task # column.',
        'Folder is task-specific - opens attachments only for that row.',
        'Click to open "Attachments - Task <TaskNumber>" modal.',
        'Use modal to manage task image and file attachments.',
        'Close or save modal to return to grid.'
      ],
      tip: 'Each row has its own folder icon - never confuse rows when uploading evidence.',
      tags: ['folder', 'icon', 'open', 'attachment', 'task', 'editing', 'data'],
      top: 37,
      left: 4,
      section: 'attachments-grid-section',
      icon: 'folder-open'
    },
    {
      id: 'grid-file-count',
      label: '2',
      title: 'Attachment File Count',
      short: '"(N file)" text indicates how many attachments exist for that task.',
      steps: [
        'Look next to the folder icon for "(N file)" or "0 file" text.',
        'Number indicates total attachments saved for that task.',
        'Count updates automatically after modal Save + form Save.',
        'Zero means no files attached for the row yet.',
        'Use count to spot tasks missing required evidence.'
      ],
      tip: 'During reviews, scan file counts to catch tasks with no evidence attached.',
      tags: ['attachment', 'count', 'indicator', 'task', 'data'],
      top: 45,
      left: 7,
      section: 'attachments-grid-section',
      icon: 'hashtag'
    },
    {
      id: 'grid-task-image-thumbnail',
      label: '3',
      title: 'Inline Task Image Thumbnail',
      short: 'Tasks with a saved image show an inline thumbnail right under the file count.',
      steps: [
        'Tasks with task image show small image preview in Task # cell.',
        'Click the thumbnail to open image preview popup.',
        'Use popup to edit, replace, or remove the task image.',
        'Tasks without an image show no thumbnail.',
        'Save form after image changes to persist association.'
      ],
      tip: 'Thumbnails give a quick visual scan of which tasks already have evidence images.',
      tags: ['image', 'thumbnail', 'preview', 'task', 'editing', 'data'],
      top: 58,
      left: 7,
      section: 'attachments-grid-section',
      icon: 'image'
    },

    // ========================================
    // TASK IMAGE MODAL (existing image) FEATURES
    // (Sub-hotspots shown after clicking A2 or A4)
    // ========================================
    {
      id: 'modal-title-task-number',
      label: '1',
      title: 'Modal Title & Task Number',
      short: 'Title bar shows exactly which task number is currently being edited.',
      steps: [
        'Read modal title "Attachments - Task <TaskNumber>" at top.',
        'Confirm task number matches the row you intended to update.',
        'Close immediately if it does not match expected task.',
        'Reopen attachment from correct row.',
        'Continue with image/attachment edits.'
      ],
      tip: 'Always verify task number before uploading task-specific evidence.',
      tags: ['modal', 'title', 'task', 'navigation', 'data'],
      top: 8,
      left: 38,
      section: 'task-image-modal-section',
      icon: 'window-maximize'
    },
    {
      id: 'modal-current-task-image',
      label: '2',
      title: 'Current Task Image Preview',
      short: 'Preview of currently saved task image with source badges next to it.',
      steps: [
        'Look at the image preview area under "Task Image".',
        'This is the image that will be associated with the task.',
        'Click the preview to open full-size popup.',
        'Use popup to validate or replace the image.',
        'Save form to keep the image associated.'
      ],
      tip: 'Use full-size preview to ensure image is clear enough for review.',
      tags: ['image', 'preview', 'task', 'data'],
      top: 30,
      left: 42,
      section: 'task-image-modal-section',
      icon: 'image'
    },
    {
      id: 'modal-source-badges',
      label: '3',
      title: 'Source Badges (ProPak / ClientDash)',
      short: 'Badges next to image show whether it came from ProPak or ClientDash.',
      steps: [
        'Locate badge near image like "Source: ProPak" or "Source: ClientDash".',
        'ProPak indicates image was fetched from ProPak workpack.',
        'ClientDash indicates image was uploaded directly in ClientDash.',
        'Use this to maintain origin traceability for evidence.',
        'Replace image if source does not match required context.'
      ],
      tip: 'Source labels are critical for evidence audits - keep them aligned with workflow source.',
      tags: ['image', 'source', 'badge', 'propak', 'clientdash', 'traceability', 'data'],
      top: 17,
      left: 43,
      section: 'task-image-modal-section',
      icon: 'tag'
    },
    {
      id: 'modal-remove-task-image',
      label: '4',
      title: 'Remove Task Image',
      short: 'Use Remove Task Image to clear the current image before setting a new one.',
      steps: [
        'Click "Remove Task Image" button under image preview.',
        'Image clears and modal shows upload state.',
        'Use upload area or ProPak toggle to set new image.',
        'Confirm new image preview before saving.',
        'Save modal then form to persist removal.'
      ],
      tip: 'You must remove existing image first before uploading a new one.',
      tags: ['image', 'remove', 'replace', 'task', 'editing', 'data'],
      top: 36,
      left: 35,
      section: 'task-image-modal-section',
      icon: 'times-circle'
    },
    {
      id: 'modal-add-files-button',
      label: '5',
      title: 'Add Files Button',
      short: 'Click Add files to browse and select files from your PC for attachment.',
      steps: [
        'Click "Add files" button under Attachments header.',
        'Browse and select supported files (Images, PDF, XLSX, CSV).',
        'Selected files appear in the attachment list.',
        'Files are uploaded only after clicking Save.',
        'Save modal then form to persist files.'
      ],
      tip: 'Use Add files for explicit selection; use drop zone for drag/drop bulk upload.',
      tags: ['attachment', 'add', 'files', 'browse', 'upload', 'editing', 'data'],
      top: 47,
      left: 37,
      section: 'task-image-modal-section',
      icon: 'plus-circle'
    },
    {
      id: 'modal-drop-zone',
      label: '6',
      title: 'Drag & Drop Zone',
      short: 'Drag files from PC into the highlighted drop zone to add them to attachments.',
      steps: [
        'Drag files from your file explorer.',
        'Drop them into the highlighted drop zone in the modal.',
        'Files appear in the attachment list immediately.',
        'Drop zone supports multiple files at once.',
        'Save modal then form to persist new attachments.'
      ],
      tip: 'Drag-drop is faster than browsing for multiple files.',
      tags: ['drag', 'drop', 'attachment', 'upload', 'editing', 'data'],
      top: 56,
      left: 50,
      section: 'task-image-modal-section',
      icon: 'cloud-upload-alt'
    },
    {
      id: 'modal-attachment-item',
      label: '7',
      title: 'Attachment Item & Open Link',
      short: 'Each attachment shows file name, badges, an Open link, and remove control.',
      steps: [
        'Each attached file appears as a row in the attachment list.',
        'Click "Open" or file name to view file content.',
        'Badges (e.g., "task image", "ProPak") indicate type/source.',
        'Use to verify correct file is attached before saving.',
        'Click X to remove the file from list.'
      ],
      tip: 'Open files after upload to confirm correct content.',
      tags: ['attachment', 'open', 'item', 'badge', 'data'],
      top: 70,
      left: 49,
      section: 'task-image-modal-section',
      icon: 'external-link-alt'
    },
    {
      id: 'modal-remove-attachment',
      label: '8',
      title: 'Remove Attachment (X)',
      short: 'X icon next to attachment removes it from the list.',
      steps: [
        'Find X icon at right side of an attachment row.',
        'Click X to remove the file from the list.',
        'Removal applies after Save in modal.',
        'Use to remove wrong/unwanted file before save.',
        'Re-add via drop zone or Add files if needed.'
      ],
      tip: 'Removing in modal is undoable until you click Save.',
      tags: ['attachment', 'remove', 'delete', 'editing', 'data'],
      top: 69,
      left: 64.5,
      section: 'task-image-modal-section',
      icon: 'times'
    },
    {
      id: 'modal-cancel-button',
      label: '9',
      title: 'Cancel (Discard Changes)',
      short: 'Cancel exits modal without applying any changes made inside it.',
      steps: [
        'Click "Cancel" in modal footer.',
        'All modal changes (add/remove) are discarded.',
        'Modal closes and returns to readiness grid.',
        'Use Cancel when you opened wrong row by mistake.',
        'Reopen correct row to continue.'
      ],
      tip: 'Cancel is safe - never persists changes.',
      tags: ['cancel', 'discard', 'modal', 'navigation'],
      top: 78,
      left: 59,
      section: 'task-image-modal-section',
      icon: 'ban'
    },
    {
      id: 'modal-save-button',
      label: '10',
      title: 'Save (Apply to Row)',
      short: 'Save in modal footer applies attachment/image changes to the selected row.',
      steps: [
        'Click "Save" in modal footer to apply changes to row.',
        'Modal closes and grid shows updated attachments.',
        'Click form Save in toolbar to persist to backend.',
        'Skipping form Save loses modal changes.',
        'Verify file count and thumbnail updated correctly.'
      ],
      tip: 'Modal Save is step 1 - form Save is step 2. Always do both.',
      tags: ['save', 'modal', 'apply', 'persist', 'workflow', 'data'],
      top: 82,
      left: 65,
      section: 'task-image-modal-section',
      icon: 'save'
    },

    // ========================================
    // TASK IMAGE UPLOAD MODAL FEATURES
    // (Sub-hotspots shown after clicking A3)
    // ========================================
    {
      id: 'upload-propak-toggle',
      label: '1',
      title: 'Get Image from ProPak Toggle',
      short: 'Toggle to fetch task image reference from ProPak workpack.',
      steps: [
        'Find "Get image from ProPak" toggle near Task Image header.',
        'Switch on to fetch image from associated ProPak workpack.',
        'Wait for ProPak fetch state to complete.',
        'Use fetched image as task image or as attachment.',
        'Save modal and form to commit selection.'
      ],
      tip: 'Use ProPak source for visual continuity between systems.',
      tags: ['propak', 'toggle', 'image', 'fetch', 'integration', 'data'],
      top: 18,
      left: 65,
      section: 'task-image-upload-modal-section',
      icon: 'sync'
    },
    {
      id: 'upload-image-drop-zone',
      label: '2',
      title: 'Drag & Drop Image Zone',
      short: 'Drag image directly into highlighted area or click button below.',
      steps: [
        'Drag an image file from your file explorer.',
        'Drop it into the highlighted image upload zone.',
        'Allowed formats: PNG, JPG, JPEG, GIF, WEBP.',
        'Verify image preview displays correctly.',
        'Save modal and form to commit task image.'
      ],
      tip: 'Drag/drop is fastest for single image upload.',
      tags: ['drag', 'drop', 'image', 'upload', 'editing', 'data'],
      top: 32,
      left: 50,
      section: 'task-image-upload-modal-section',
      icon: 'image'
    },
    {
      id: 'upload-task-image-button',
      label: '3',
      title: 'Upload Task Image Button',
      short: 'Click to browse and select an image from your PC for task image.',
      steps: [
        'Click "Upload Task Image" button under drop zone.',
        'Browse and pick a supported image file.',
        'Image preview replaces drop zone after selection.',
        'Verify preview is correct image.',
        'Save modal and form to persist task image.'
      ],
      tip: 'Use this button when drag-drop is not convenient.',
      tags: ['image', 'upload', 'browse', 'task', 'editing', 'data'],
      top: 40,
      left: 38,
      section: 'task-image-upload-modal-section',
      icon: 'upload'
    },
    {
      id: 'upload-attachments-add-files',
      label: '4',
      title: 'Add Files (Attachments)',
      short: 'In upload state, add general attachments along with the task image.',
      steps: [
        'Find "Add files" button under Attachments header.',
        'Browse and select Images, PDF, XLSX, or CSV.',
        'Selected files appear in attachment list.',
        'Image goes to Task Image; other files become attachments.',
        'Save modal and form to persist all selections.'
      ],
      tip: 'Use task image area for primary visual; use Attachments for supporting docs.',
      tags: ['attachment', 'add', 'files', 'upload', 'editing', 'data'],
      top: 50,
      left: 38,
      section: 'task-image-upload-modal-section',
      icon: 'plus-circle'
    },
    {
      id: 'upload-attachments-drop-zone',
      label: '5',
      title: 'Attachments Drop Zone',
      short: 'Drag multiple files into the attachment drop zone in one action.',
      steps: [
        'Drag files from PC.',
        'Drop into the attachments drop area.',
        'Multiple files supported in one drop.',
        'Each file appears as a row in attachment list.',
        'Save modal and form to persist all attachments.'
      ],
      tip: 'Use bulk drop to attach an evidence pack quickly.',
      tags: ['drag', 'drop', 'bulk', 'attachment', 'upload', 'editing', 'data'],
      top: 65,
      left: 50,
      section: 'task-image-upload-modal-section',
      icon: 'cloud-upload-alt'
    },
    {
      id: 'upload-cancel-button',
      label: '6',
      title: 'Cancel (Upload State)',
      short: 'Cancel exits without uploading any image or attachments.',
      steps: [
        'Click "Cancel" in modal footer.',
        'All staged uploads are discarded.',
        'Modal closes and returns to grid.',
        'Use Cancel when wrong row was opened.',
        'Reopen correct row to upload.'
      ],
      tip: 'Cancel is safe even after staging files - nothing is uploaded yet.',
      tags: ['cancel', 'discard', 'upload', 'modal', 'navigation'],
      top: 77,
      left: 59,
      section: 'task-image-upload-modal-section',
      icon: 'ban'
    },
    {
      id: 'upload-save-button',
      label: '7',
      title: 'Save (Apply Upload)',
      short: 'Save uploads the staged image and attachments to the row.',
      steps: [
        'Click "Save" in modal footer.',
        'Image uploads and attachments are committed to row.',
        'Click form Save in toolbar to persist to backend.',
        'Verify thumbnail and file count updated in grid.',
        'Banner clears once form Save completes.'
      ],
      tip: 'Watch for unsaved image warning banner if you skip form Save.',
      tags: ['save', 'upload', 'modal', 'apply', 'workflow', 'data'],
      top: 77,
      left: 65,
      section: 'task-image-upload-modal-section',
      icon: 'save'
    }
  ];

  // IDs of hotspots that should appear on the overview image (in addition to sections)
  overviewImageHotspots = [
    'row-selection',
    'row-actions',
    'highlight-changed-cell',
    'attachments-folder',
    'task-image-thumbnail',
    'task-image-upload',
    'attachment-modal-workflow'
  ];

  // Section IDs that are linked-to from overview hotspots (A1-A4) and should
  // NOT appear as numbered section pings on the overview image.
  hiddenOverviewSectionIds = [
    'task-checked',
    'attachments-grid-section',
    'task-image-modal-section',
    'task-image-upload-modal-section'
  ];

  get visibleHotspots(): any[] {
    if (this.currentSection) {
      return this.hotspots.filter(h => h.section === this.currentSection);
    } else {
      // In overview mode: show section markers (excluding hidden ones) + specific overview hotspots
      const visibleSections = this.sections.filter(s => !this.hiddenOverviewSectionIds.includes(s.id));
      const specificHotspots = this.hotspots.filter(h => this.overviewImageHotspots.includes(h.id));
      return [...visibleSections, ...specificHotspots];
    }
  }

  // Computed: filtered hotspots for sidebar
  get filteredHotspots(): ReadinessHotspot[] {
    let filtered = this.hotspots;

    // Filter by category
    if (this.activeCategory !== 'all') {
      filtered = filtered.filter(h => h.tags.includes(this.activeCategory));
    }

    // Filter by search term
    const q = this.searchTerm ? this.searchTerm.trim().toLowerCase() : '';
    if (q) {
      filtered = filtered.filter(h => {
        const text = (h.title + ' ' + h.short + ' ' + h.tags.join(' ')).toLowerCase();
        return text.indexOf(q) !== -1;
      });
    }

    return filtered;
  }

  // Get features count by category
  getCategoryCount(categoryId: string): number {
    if (categoryId === 'all') return this.hotspots.length;
    return this.hotspots.filter(h => h.tags.includes(categoryId)).length;
  }

  selectedHotspot: ReadinessHotspot = this.hotspots.find(x => x.title === 'Row Selection Checkbox') || this.hotspots[0];
  imageLoaded = true;
  imagesPreloaded = false;

  constructor(private location: Location) {
    this.preloadAllImages();
  }

  ngOnInit(): void {
    this.selectedHotspot = this.hotspots.find(x => x.title === 'Row Selection Checkbox') || this.hotspots[0];
  }

  preloadAllImages(): void {
    // Images are now in the DOM, just wait for them to load
    // This is handled by the browser automatically
    // We just need to track when they're ready
    const imagesToPreload = [
      'app/assets/readiness-guide/overview-latest.png',
      ...this.sections.map(s => s.image)
    ];

    let loadedCount = 0;
    const totalImages = imagesToPreload.length;

    imagesToPreload.forEach(src => {
      const img = new Image();
      img.onload = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          this.imagesPreloaded = true;
        }
      };
      img.onerror = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          this.imagesPreloaded = true;
        }
      };
      img.src = src;
    });
  }

  goBack(): void {
    this.location.back();
  }

  selectHotspot(spot: ReadinessHotspot): void {
    console.log('selectHotspot called:', spot.title, 'section:', spot.section, 'linkedSection:', spot.linkedSection);
    this.selectedHotspot = spot;

    // If hotspot has a linkedSection, open that section
    if (spot.linkedSection) {
      console.log('Opening linkedSection:', spot.linkedSection);
      this.openSection(spot.linkedSection);
    }
    // If hotspot belongs to a section, switch to that section's image
    else if (spot.section) {
      console.log('Setting currentSection to:', spot.section);
      this.currentSection = spot.section;
    }
    // If hotspot has no section (overview-level), go back to overview
    else if (spot.section === null) {
      console.log('Setting currentSection to null (overview)');
      this.currentSection = null;
    }
    console.log('currentSection is now:', this.currentSection);
  }

  setCategory(categoryId: string): void {
    this.activeCategory = categoryId;
  }

  openSection(sectionId: string): void {
    const section = this.sections.find(s => s.id === sectionId);
    if (section) {
      if (this.currentSection === sectionId) {
        // Going back to overview - instant switch
        this.currentSection = null;
      } else {
        // Opening a section - instant switch
        this.currentSection = sectionId;
        const firstFeature = this.hotspots.find(h => h.section === sectionId);
        if (firstFeature) {
          this.selectedHotspot = firstFeature;
        }
      }
    }
    else if (section == undefined) {

    }
  }

  onImageClick(event: MouseEvent, img: HTMLImageElement): void {
    if (this.currentSection) {
      // Going back to overview - instant switch
      this.currentSection = null;
      this.selectedHotspot = this.hotspots.find(x => x.title === 'Row Selection Checkbox') || this.hotspots[0];
      event.stopPropagation();
    } else {
      this.onMapClick(event, img);
    }
  }

  // Dev helper: click image to get coordinates
  onMapClick(event: MouseEvent, img: HTMLImageElement): void {
    const rect = img.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const leftPercent = (x / rect.width) * 100;
    const topPercent = (y / rect.height) * 100;

    console.log('Hotspot position:', {
      top: +topPercent.toFixed(1),
      left: +leftPercent.toFixed(1)
    });
  }

  isSection(item: any): item is Section {
    return 'image' in item && 'description' in item;
  }

  isHotspot(item: any): item is ReadinessHotspot {
    return 'steps' in item;
  }

  getCurrentSectionInfo(): Section | null {
    if (!this.currentSection) return null;
    return this.sections.find(s => s.id === this.currentSection) || null;
  }
}
